import React, { useRef, useEffect } from "react";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";

const GarmentMeasurementUpload: React.FC = () => {
  const { serviceInterface, serviceConfig, garmentMeasurement } = useUnifiedService();
  const { setCanProceedToNextStep } = serviceInterface;

  const multipleImagesInputRef = useRef<HTMLInputElement>(null);
  const singleImageInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const maxImagesCount = serviceConfig.upload?.maxImages || 50;
  const instructions = serviceConfig.upload?.photoDescriptions?.[0]?.instructions || [];

  useEffect(() => {
    const validImagesCount = garmentMeasurement.images.filter((img) => img.isValid && img.base64).length;
    const hasErrors = garmentMeasurement.images.some((img) => img.error);
    const canProceed = validImagesCount > 0 && !hasErrors;

    setCanProceedToNextStep(canProceed);
  }, [garmentMeasurement.images]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      garmentMeasurement.handleImageUpload(files[0], index);
    }
  };

  const handleMultipleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      garmentMeasurement.handleMultipleImagesUpload(files);

      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleRemoveAll = () => {
    garmentMeasurement.resetGarmentMeasurement();

    if (multipleImagesInputRef.current) {
      multipleImagesInputRef.current.value = "";
    }

    singleImageInputRefs.current.forEach((input) => {
      if (input) {
        input.value = "";
      }
    });
  };

  const validImagesCount = garmentMeasurement.images.filter((img) => img.isValid && img.base64).length;

  return (
    <div className="flex flex-col items-center w-full gap-4 md:gap-16">
      <div className="flex flex-col w-full max-w-2xl gap-4 p-4 border border-gray-200 rounded-lg">
        <div className="flex flex-col items-center justify-between gap-2 md:flex-row">
          <div className="flex items-center gap-2 font-bold text-center md:text-left text-secondary">
            <h2>Make sure to follow these instructions when uploading your garment images</h2>
          </div>
          {/* <button className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 transition-colors rounded-lg bg-blue-50 hover:bg-blue-100 min-w-fit" onClick={() => {}}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>View Guide</span>
          </button> */}
        </div>
        <ul className="space-y-2 text-xs text-gray-600 list-disc ps-6">
          {instructions.map((instruction, index) => (
            <li key={index}>{instruction}</li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col items-center justify-center w-full gap-4 md:flex-row">
        <button className="flex items-center gap-2 px-4 py-2 transition-colors border rounded-lg text-primary bg-primary/10 border-primary/20 hover:bg-primary/20">
          <label htmlFor="multipleGarmentImages" className="flex items-center justify-center h-full min-w-full gap-2 cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>Upload Multiple Images</span>
          </label>
        </button>
        <input id="multipleGarmentImages" type="file" className="hidden" onChange={handleMultipleImagesChange} accept="image/*" multiple ref={multipleImagesInputRef} />

        <button
          className="flex items-center gap-2 px-4 py-2 text-red-600 transition-colors border border-red-200 rounded-lg bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleRemoveAll}
          disabled={validImagesCount === 0}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          <span>Remove All</span>
        </button>
      </div>

      <div className="grid w-full max-w-5xl grid-cols-1 gap-6 md:grid-cols-3 sm:grid-cols-2 lg:grid-cols-4">
        {garmentMeasurement.images.map((image, index) => (
          <div key={index} className="flex flex-col gap-2">
            <div className="relative h-[15rem]">
              <div className="relative h-full overflow-hidden border-2 border-gray-200 rounded-lg size-full ">
                {image.preview || image.base64 ? (
                  <img src={image.preview || `data:image/png;base64,${image.base64}`} alt={`Uploaded garment ${index + 1}`} className="object-contain w-full h-full max-h-[15rem]" />
                ) : (
                  <div className="grid w-full h-full place-content-center">
                    <img src={serviceConfig.upload?.placeholderImages?.[0] || "/images/default/default-garment.png"} alt="Default garment" className="object-contain w-full h-full max-h-[15rem]" />
                  </div>
                )}
              </div>

              {garmentMeasurement.images.length > 1 && (
                <button
                  onClick={() => garmentMeasurement.removeImage(index)}
                  className="absolute z-20 p-2 transition-colors bg-white border border-gray-200 rounded-full shadow-sm top-2 right-2 hover:bg-red-50 hover:border-red-200"
                  aria-label="Remove image"
                >
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>

            <div className="flex flex-col w-full gap-2">
              <button className="flex items-center justify-center gap-2 px-4 py-2 font-medium text-white transition-all duration-300 rounded-lg bg-primary hover:bg-primary-dark hover:shadow-md">
                <label htmlFor={`garmentImage-${index}`} className="flex items-center justify-center h-full min-w-full gap-2 cursor-pointer">
                  {image.preview || image.base64 ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      <span>Change Image</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span>Upload Image {index + 1}</span>
                    </>
                  )}
                </label>
              </button>

              <input
                id={`garmentImage-${index}`}
                type="file"
                className="hidden"
                onChange={(e) => handleImageChange(e, index)}
                accept="image/*"
                ref={(el) => (singleImageInputRefs.current[index] = el)}
              />

              {image.error && <div className="p-2 text-sm text-red-600 border border-red-200 rounded-md bg-red-50">{image.error}</div>}
            </div>
          </div>
        ))}

        {garmentMeasurement.images.length < maxImagesCount && (
          <div
            className="flex flex-col items-center justify-center w-full h-[15rem] transition-colors border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-gray-400"
            onClick={garmentMeasurement.addNewImageSlot}
          >
            <svg className="w-10 h-10 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <p className="text-xs font-medium text-gray-500">Add More Images</p>
            <p className="mt-1 text-xs text-gray-400">
              ({garmentMeasurement.images.length}/{maxImagesCount})
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GarmentMeasurementUpload;
